import React, { useMemo } from "react";
import ContentAvatar from "@src/deprecatedDesignSystem/components/ContentAvatar";
import { renderTrainingResourceAvatar } from "@src/components/contentLibrary/utils";
import { LibraryItemType } from "@src/types.generated";
import {
  ChatAttachmentLibraryItemFragment,
  ChatAttachmentMediaFragment,
} from "./index.generated";
import { FileIcon } from "lucide-react";

export interface ChatThumbnailProps {
  libraryItemFragment?: ChatAttachmentLibraryItemFragment | null;
  mediaFragment?: ChatAttachmentMediaFragment | null;
  url?: string | null;
  file?: File;
  size?: number;
  className?: string;
}

export const ChatThumbnail: React.FC<ChatThumbnailProps> = ({
  libraryItemFragment,
  mediaFragment,
  url,
  file,
  size = 24,
  className,
}) => {
  const thumbnail = useMemo(() => {
    // File type
    if (file) {
      return (
        <ContentAvatar
          size={size}
          contentType="Resource"
          style={{ borderRadius: 4 }}
          defaultBackgroundHashKey={file.name}
        />
      );
    }

    // LibraryItem type
    if (libraryItemFragment) {
      const itemType = libraryItemFragment.type;

      if (itemType === LibraryItemType.TrainingResource) {
        return renderTrainingResourceAvatar(libraryItemFragment, {
          size,
          style: { borderRadius: 4 },
        });
      } else if (itemType === LibraryItemType.Course) {
        return (
          <ContentAvatar
            size={size}
            contentType="Course"
            style={{ borderRadius: 4 }}
            coverImage={libraryItemFragment.coverImage}
            defaultBackgroundHashKey={libraryItemFragment.id}
          />
        );
      } else {
        // Default for other library item types
        return (
          <ContentAvatar
            size={size}
            contentType="Module"
            style={{ borderRadius: 4 }}
            coverImage={libraryItemFragment.coverImage}
            defaultBackgroundHashKey={libraryItemFragment.id}
          />
        );
      }
    }

    // Media type
    if (mediaFragment) {
      return (
        <ContentAvatar
          size={size}
          contentType="Resource"
          style={{ borderRadius: 4 }}
          media={{
            thumbnailImageUrl: mediaFragment.thumbnailImageUrl,
            mediaUrls: {
              en: mediaFragment.mediaUrls.en,
              myTranslation: mediaFragment.mediaUrls.en, // Fallback
            },
          }}
          defaultBackgroundHashKey={mediaFragment.id}
        />
      );
    }

    if (url) {
      return (
        <ContentAvatar
          size={size}
          contentType="Resource"
          style={{ borderRadius: 4 }}
          media={{
            mediaUrls: {
              en: url,
              myTranslation: url,
            },
          }}
          defaultBackgroundHashKey={url}
        />
      );
    }

    return <FileIcon size={"16px"} className="text-gray-8" />;
  }, [libraryItemFragment, mediaFragment, url, size, file]);

  return (
    <div
      className={`flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      {thumbnail}
    </div>
  );
};
